import { isValidDate } from '../shared/utils';

const REVISION_REQUIRED_FIELD_KEYS = ['title', 'description', 'keywords', 'assignees', 'flag', 'rating', 'color'];

export default function hasRevisionRequiredFields(role = {}) {
  const requiredFields = { ...(role.requiredFields || {}) };

  if (requiredFields['title+description']) {
    return true;
  }

  if (REVISION_REQUIRED_FIELD_KEYS.some((fieldKey) => Boolean(requiredFields[fieldKey]))) {
    return true;
  }

  const meta = requiredFields.meta || {};
  return Object.keys(meta).some((title) => Boolean(meta[title]));
}

function unwrapCustomFieldValue(value) {
  if (value && typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
    return value.value;
  }
  return value;
}

/** flag and color are string marks on the server, anything else must not reach createRevision */
function normalizeAssetMark(value) {
  return typeof value === 'string' && value ? value : null;
}

function cloneRevisionFields(value) {
  if (Array.isArray(value)) return value.map(cloneRevisionFields);
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((result, [key, nestedValue]) => ({
      ...result,
      [key]: cloneRevisionFields(nestedValue),
    }), {});
  }
  return value;
}

export function getRevisionFieldsInitialValues(asset = {}) {
  const meta = asset.meta || {};
  const customFields = Object.keys(meta).reduce((result, title) => ({
    ...result,
    [title]: unwrapCustomFieldValue(meta[title]),
  }), {});

  return {
    title: asset.title || '',
    description: asset.description || '',
    keywords: Array.isArray(asset.keywords) ? asset.keywords : [],
    assignees: (asset.assignees || []).map((assignee) => ({
      _id: assignee.assigneeId || assignee._id,
    })),
    flag: asset.flag ?? null,
    color: asset.color ?? null,
    rating: asset.rating ?? null,
    customFields,
  };
}

export function buildImportFieldsFromRevisionAsset(
  asset = {},
  requiredCustomFieldTitles = [],
  customFieldSchemas = [],
) {
  const initialValues = getRevisionFieldsInitialValues(asset);
  const schemasByTitle = (customFieldSchemas || []).reduce((result, schema) => {
    if (!schema?.title) return result;
    return { ...result, [schema.title]: schema };
  }, {});

  const customFields = (requiredCustomFieldTitles || []).reduce((result, title) => {
    const value = initialValues.customFields[title];
    const schema = schemasByTitle[title];
    if (value === undefined || value === null) {
      return { ...result, [title]: null };
    }
    return {
      ...result,
      [title]: {
        value,
        type: schema?.type,
      },
    };
  }, {});

  return {
    comment: null,
    title: initialValues.title || null,
    description: initialValues.description || null,
    keywords: initialValues.keywords,
    assignees: initialValues.assignees,
    flag: normalizeAssetMark(initialValues.flag),
    color: normalizeAssetMark(initialValues.color),
    rating: initialValues.rating,
    customFields,
  };
}

export function prefillRevisionImportFiles(
  files = [],
  assetsById = {},
  requiredCustomFieldTitles = [],
  customFieldSchemas = [],
) {
  return (files || []).map((file) => {
    if (file.action !== 'addRevision' || !file.duplicatedModel?._id) return file;
    const asset = assetsById[String(file.duplicatedModel._id)];
    if (!asset) return file;
    const revisionFields = buildImportFieldsFromRevisionAsset(
      asset,
      requiredCustomFieldTitles,
      customFieldSchemas,
    );
    return {
      ...file,
      fields: cloneRevisionFields(revisionFields),
      revisionFieldsSnapshot: cloneRevisionFields(revisionFields),
    };
  });
}

export function mergeRevisionDialogAsset(asset = {}, aggregated = {}, selectedIds = []) {
  if (selectedIds.length !== 1 || String(selectedIds[0]) !== String(asset._id)) {
    return asset;
  }

  return {
    ...asset,
    title: asset.title || aggregated.title || '',
    description: asset.description || aggregated.description || '',
    keywords: asset.keywords?.length ? asset.keywords : (aggregated.keywords || []),
    assignees: asset.assignees?.length ? asset.assignees : (aggregated.assignees || []),
    flag: asset.flag ?? aggregated.flag ?? null,
    color: asset.color ?? aggregated.color ?? null,
    rating: asset.rating ?? aggregated.rating ?? null,
    meta: { ...(aggregated.meta || {}), ...(asset.meta || {}) },
  };
}

export function getKeepExistingRevisionRequiredFields(requiredFields = {}) {
  return {
    ...requiredFields,
    comments: requiredFields.comments,
    title: false,
    description: false,
    titleAndDescription: false,
    keywords: false,
    assignees: false,
    flag: false,
    rating: false,
    color: false,
    assetMarks: false,
    customFields: [],
  };
}

export function getFileValidationRequiredFields(file = {}, requiredFields = {}) {
  if (file.action === 'addRevision' && file.keepExistingRequiredFields) {
    return getKeepExistingRevisionRequiredFields(requiredFields);
  }
  return requiredFields;
}

export function isRevisionCustomFieldValueFilled(value, type) {
  if (type === 'date' && !isValidDate(value)) {
    return false;
  }
  if (value === undefined || value === null) {
    return false;
  }
  if ((typeof value === 'string' || typeof value === 'boolean') && !value) {
    return false;
  }
  return true;
}

export function hasRequiredRevisionDialogFields(requiredFields = {}) {
  const hasBooleanRequired = Object.values(requiredFields).some((value) => value === true);
  return hasBooleanRequired || Boolean(requiredFields.customFields?.length);
}
